import { useId, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ChevronRightIcon,
  SlidersHorizontalIcon,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { DeletedLabel } from '@/shared/components/DeletedLabel';
import {
  DISCLOSURE_CHEVRON,
  DISCLOSURE_TRIGGER,
} from '@/shared/components/disclosure';
import { LIST_ROW_LINK } from '@/shared/components/listRow';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { SectionHeading } from '@/shared/components/SectionHeading';
// By path, not the feature's index: one profile marker on both AI usage tabs
// (spec 234), coloured from the Models tab's own read.
import { useWorkspaceAiModels } from '@/features/ai-models/hooks/useWorkspaceAiModels';
import {
  UNKNOWN_PROFILE_HINT,
  UNKNOWN_PROFILE_NAME,
  profileIdentities,
  type ProfileIdentity,
} from '@/features/ai-models/modelsLedger';
import { ProfileMark } from '@/features/ai-models/components/ProfileMark';
import { cn } from '@/lib/utils';
import type {
  AIModelSpendLine,
  AppliedLimitThreshold,
  BreakdownEntry,
  PeriodBounds,
  WorkspaceAIModelSpend,
  WorkspaceUsageSummary,
} from '@/lib/api/generated/model';

import { USAGE_CARD, USAGE_CARD_GAP } from '../usageLayout';
import { magnitude } from '../chartScale';
import { ownPeriodSpend, type OwnPeriodSpend } from '../limitWindow';
import { useWorkspaceSpendGroups } from '../hooks/useWorkspaceSpendGroups';
import { modelBreakdownLabel } from '../modelBreakdownLabel';
import { formatMoney, formatTokens } from '../money';
import {
  NO_PERSON_LABEL,
  entryCount,
  shareSlices,
  shareText,
  type ShareLine,
  type Slice,
} from '../shareSlices';
import {
  THRESHOLD_CHIP,
  THRESHOLD_CHIP_BOX,
  THRESHOLD_LABEL,
} from '../thresholdStyles';
import {
  connectionMarker,
  modelMarker,
  personMarker,
  workflowMarker,
} from './breakdownMarkers';

type Dimension = 'workflow' | 'person' | 'profile' | 'model' | 'connection';

interface DimensionMeta {
  value: Dimension;
  title: string;
  one: string;
  many: string;
  /** Read from the spend read (nested) rather than the page's own summary. */
  fromGroups: boolean;
  /** Drawn only when the period has two or more: one model is one 100% bar. */
  onlyWhenSeveral: boolean;
  emptyLabel: string;
}

/** The bars, top to bottom (spec 255): workflow, person and profile always. */
const DIMENSIONS: DimensionMeta[] = [
  {
    value: 'workflow',
    title: 'Workflow',
    one: 'workflow',
    many: 'workflows',
    fromGroups: true,
    onlyWhenSeveral: false,
    emptyLabel: 'No workflow produced measurable usage this period.',
  },
  {
    value: 'person',
    title: 'Person',
    one: 'person',
    many: 'people',
    fromGroups: false,
    onlyWhenSeveral: false,
    emptyLabel: 'No usage was attributed this period.',
  },
  {
    value: 'profile',
    title: 'Profile',
    one: 'profile',
    many: 'profiles',
    fromGroups: true,
    onlyWhenSeveral: false,
    emptyLabel: 'No AI profile was recorded for this period’s runs.',
  },
  {
    value: 'model',
    title: 'Model',
    one: 'model',
    many: 'models',
    fromGroups: false,
    onlyWhenSeveral: true,
    emptyLabel: 'No model was recorded for this period’s runs.',
  },
  {
    value: 'connection',
    title: 'Connection',
    one: 'connection',
    many: 'connections',
    fromGroups: true,
    onlyWhenSeveral: true,
    emptyLabel: 'No AI connection was recorded for this period’s runs.',
  },
];

/** A cap one line sits against: only a workflow can carry one (spec 080 FR-013). */
interface EntryCap {
  limit: string;
  threshold: AppliedLimitThreshold;
  /**
   * What the cap's own period spent and how to say it ("today"), when that period is
   * not the one being read (spec 237). A Daily cap read in October is judged by today,
   * and the line's own figure is the month's.
   */
  own: OwnPeriodSpend | null;
}

/** One line of a dimension, whichever read it came from. */
interface Row extends ShareLine {
  /** The name as recorded, where the line shows a friendlier one (spec 217). */
  recorded?: string;
  deleted: boolean;
  cap: EntryCap | null;
  marker?: ReactNode;
  /** A profile's own mark, drawn beside its name in the legend too (spec 255). */
  identity?: ReactNode;
  /** One line under the name saying why, for Not recorded (spec 252). */
  hint?: string;
  /** A workflow line that can be opened: its key, when it has one and lives (spec 234 US5). */
  workflowKey?: string;
  /** What a chosen slice lists under this line: profiles, or workflows (spec 255). */
  children: Row[];
}

/** What the lines need to draw a marker, beside the line itself. */
interface Lookup {
  /** Profile identities from the Models read; null while it is pending or failed. */
  identities: Map<string, ProfileIdentity> | null;
}

/**
 * Where the period's money went (spec 255): one share bar per dimension, all at once.
 *
 * It replaced one table with a five-way Group by (spec 225), two of whose groupings
 * were nested trees behind Expand all, and the only thing on the tab not drawn. The
 * Models tab's share bar answers the same question, and the reader liked it: a bar and
 * a legend of names, amounts and shares. Choosing a slice lists what it is made of
 * below its bar.
 *
 * The lines keep the service's order. A client re-sort would produce a page that
 * contradicts the API for the same period (073 FR-015).
 */
export function WhereItWent({
  workspaceId,
  period,
  summary,
  onSliceChosen,
}: {
  workspaceId: string;
  period: string | undefined;
  summary: WorkspaceUsageSummary;
  /** A slice was chosen in this dimension's bar (spec 257 follows it). */
  onSliceChosen?: (dimension: Dimension) => void;
}) {
  const headingId = useId();
  const groups = useWorkspaceSpendGroups(workspaceId, period);
  // The Models tab's read, for the profiles' marks (spec 234 R3): never waited on —
  // pending or failed, profile lines keep the plain tile.
  const models = useWorkspaceAiModels(workspaceId, period);
  const lookup: Lookup = {
    identities: models.data ? profileIdentities(models.data.entries) : null,
  };
  const currency = summary.spend.currency ?? 'USD';
  const [chosen, setChosen] = useState<Partial<Record<Dimension, string>>>({});

  const bars = DIMENSIONS.flatMap((meta) => {
    if (meta.fromGroups && groups.data === undefined) return [];
    const rows = rowsFor(meta.value, summary, groups.data, lookup);
    if (meta.onlyWhenSeveral && rows.length < 2) return [];
    return [{ meta, rows }];
  });

  return (
    <section
      aria-labelledby={headingId}
      className={`${USAGE_CARD} flex flex-col ${USAGE_CARD_GAP}`}
    >
      <SectionHeading as="h2" id={headingId}>
        Where it went
      </SectionHeading>
      {groups.isPending ? (
        <PendingRegion label="Loading where the spend went" className="gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </PendingRegion>
      ) : (
        <>
          {groups.isError ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-muted-foreground text-sm">
                Spend by workflow, profile and connection could not be read just
                now.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void groups.refetch()}
                disabled={groups.isFetching}
              >
                {groups.isFetching ? 'Trying…' : 'Try again'}
              </Button>
            </div>
          ) : null}
          <div className="flex flex-col gap-6">
            {bars.map(({ meta, rows }) => (
              <ShareBar
                key={meta.value}
                meta={meta}
                rows={rows}
                currency={currency}
                workspaceId={workspaceId}
                chosen={chosen[meta.value] ?? null}
                onChoose={(id) => {
                  if (chosen[meta.value] !== id) onSliceChosen?.(meta.value);
                  setChosen((previous) => ({
                    ...previous,
                    [meta.value]: previous[meta.value] === id ? undefined : id,
                  }));
                }}
              />
            ))}
          </div>
          {bars.some(({ rows }) => rows.length > 0) ? (
            <AsTable bars={bars} currency={currency} />
          ) : null}
        </>
      )}
    </section>
  );
}

/** What the lines under a line are, drawn small beside each name. */
const PROFILE = SlidersHorizontalIcon;
const WORKFLOW = WorkflowIcon;

/** A dimension's lines, in the service's order. */
function rowsFor(
  dimension: Dimension,
  summary: WorkspaceUsageSummary,
  groups: WorkspaceAIModelSpend | undefined,
  lookup: Lookup,
): Row[] {
  // A profile's line wears its mark from the Models read: letter and dot (spec 234
  // US1); the unknown, the glyph; a slug the read does not hold, the plain icon.
  const profileMarkOf = (line: AIModelSpendLine) => {
    if (line.key === null) {
      return (
        <ProfileMark identity={{ slot: 'unknown', letter: null }} size="xs" />
      );
    }
    const identity = lookup.identities?.get(line.key);
    if (identity) return <ProfileMark identity={identity} size="xs" />;
    return (
      <PROFILE className="size-3.5" strokeWidth={1.75} aria-hidden="true" />
    );
  };
  const asWorkflow = (row: Row, line: AIModelSpendLine): Row => ({
    ...row,
    workflowKey: line.key !== null && !line.deleted ? line.key : undefined,
  });
  // The runs with no profile are a gap in the data, said as on the Models tab
  // (spec 252): "Not recorded", with why.
  const notRecorded = (line: AIModelSpendLine): Partial<Row> =>
    line.key === null
      ? { label: UNKNOWN_PROFILE_NAME, hint: UNKNOWN_PROFILE_HINT }
      : {};
  const asProfile = (row: Row, line: AIModelSpendLine): Row => ({
    ...row,
    ...notRecorded(line),
    marker: profileMarkOf(line),
  });
  switch (dimension) {
    case 'workflow': {
      // A workflow's cap comes from the usage summary, which carries it (spec 080);
      // the spend read names the same workflows by the same key.
      const caps = new Map(
        summary.by_workflow.map((entry) => [
          entry.key,
          capOf(entry, summary.period),
        ]),
      );
      return (groups?.by_workflow ?? []).map((entry) => ({
        ...asWorkflow(fromLine(entry), entry),
        cap: caps.get(entry.key) ?? null,
        marker: workflowMarker(),
        children: entry.children.map((line) => asProfile(fromLine(line), line)),
      }));
    }
    case 'profile':
      return (groups?.by_profile ?? []).map((entry) => ({
        ...asProfile(fromLine(entry), entry),
        identity: profileMarkOf(entry),
        children: entry.children.map((line) =>
          asWorkflow(fromLine(line, WORKFLOW), line),
        ),
      }));
    case 'connection':
      return (groups?.by_connection ?? []).map((entry) => ({
        ...fromLine(entry),
        marker: connectionMarker(),
        children: entry.children.map((line) => asProfile(fromLine(line), line)),
      }));
    case 'model':
      return summary.by_model.map((entry) => {
        const label = modelBreakdownLabel(entry);
        return {
          ...fromEntry(entry),
          label,
          // The id as recorded stays on the name: the admin console prices a model
          // by it (spec 217).
          recorded: label === entry.label ? undefined : entry.label,
          marker: modelMarker(),
        };
      });
    case 'person':
      return summary.by_person.map((entry) => ({
        ...fromEntry(entry),
        // The contract's unattributed bucket: a gap, not a person (spec 255).
        label: entry.key === null ? NO_PERSON_LABEL : entry.label,
        marker: personMarker(entry),
      }));
  }
}

function fromLine(line: AIModelSpendLine, icon?: LucideIcon): Row {
  const Icon = icon;
  return {
    key: line.key,
    label: line.label,
    marker: Icon ? (
      <Icon className="size-3.5" strokeWidth={1.75} aria-hidden="true" />
    ) : undefined,
    // A key-less line names an absence: "Profile not known", "Connection not known".
    unknown: line.key === null,
    deleted: line.deleted,
    amount: line.amount,
    tokens: line.total_tokens,
    runs: line.run_count,
    unmeasured: line.unmeasured_run_count,
    unpriced: line.unpriced_run_count,
    cap: null,
    children: [],
  };
}

function fromEntry(entry: BreakdownEntry): Row {
  return {
    key: entry.key,
    label: entry.label,
    unknown: entry.key === null,
    deleted: false,
    amount: entry.amount,
    tokens: entry.total_tokens,
    runs: entry.run_count,
    unmeasured: entry.unmeasured_run_count,
    // The summary's breakdowns do not count unpriced runs per line; the meter's
    // caveat states the period's.
    unpriced: 0,
    cap: null,
    children: [],
  };
}

function capOf(
  entry: WorkspaceUsageSummary['by_workflow'][number],
  period: PeriodBounds,
): EntryCap | null {
  // Null exactly together: the contract pairs them, and a threshold with no limit
  // beside it would be a state against nothing.
  if (
    entry.limit === null ||
    entry.limit === undefined ||
    entry.threshold === null ||
    entry.threshold === undefined
  )
    return null;
  return {
    limit: entry.limit,
    threshold: entry.threshold,
    own: ownPeriodSpend(
      entry.limit_period,
      period,
      entry.limit_consumed_amount,
    ),
  };
}

/**
 * A slice's legend entry: a toggle (spec 255). Its reach is the coarse-pointer floor
 * (`touch-target`), sized by padding rather than a min height, which would override it.
 */
const SLICE_TOGGLE = `touch-target inline-flex max-w-full items-center gap-1.5 rounded-sm px-1 py-0.5 text-left text-sm transition-colors hover:bg-muted/60 aria-pressed:bg-muted ${FOCUS_RING}`;

/** What a line or slice cost, or why it states no cost (spec 216). */
function spendText(
  lines: Pick<Row, 'amount' | 'runs' | 'unmeasured' | 'unpriced'>[],
  amount: string,
  currency: string,
): { text: string; stated: boolean } {
  const runs = lines.reduce((sum, line) => sum + line.runs, 0);
  const unmeasured = lines.reduce((sum, line) => sum + line.unmeasured, 0);
  const unpriced = lines.reduce((sum, line) => sum + line.unpriced, 0);
  // A line whose every run went unmeasured has no cost to state; "$0.00" would say
  // the work was free. One whose every run had no price is the same.
  if (runs > 0 && unmeasured === runs)
    return { text: 'Not measured', stated: false };
  if (runs > 0 && unmeasured + unpriced === runs && magnitude(amount) === 0)
    return { text: 'No price', stated: false };
  return { text: formatMoney(amount, currency), stated: true };
}

/** One dimension's bar, its legend, and the chosen slice listed below (spec 255). */
function ShareBar({
  meta,
  rows,
  currency,
  workspaceId,
  chosen,
  onChoose,
}: {
  meta: DimensionMeta;
  rows: Row[];
  currency: string;
  workspaceId: string;
  chosen: string | null;
  onChoose: (id: string) => void;
}) {
  const headingId = useId();
  const listId = useId();
  const slices = shareSlices(rows);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const selected = slices.find((slice) => slice.id === chosen) ?? null;
  return (
    <div
      role="group"
      aria-labelledby={headingId}
      data-dimension={meta.value}
      className="flex min-w-0 flex-col gap-2"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 id={headingId} className="text-sm font-semibold">
          {meta.title}
        </h3>
        <span className="text-muted-foreground text-xs tabular-nums">
          {entryCount(rows.length, meta.one, meta.many)}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{meta.emptyLabel}</p>
      ) : (
        <>
          {total > 0 ? (
            /*
              A picture of the legend, not a replacement for it: every slice is also
              written out below. A slice under 1% is listed and not drawn. A segment
              chooses its slice for a pointer; the legend's toggle is the control for
              the keyboard and the screen reader.
            */
            <div
              aria-hidden="true"
              data-share-bar=""
              className="bg-muted flex h-3 w-full gap-px overflow-hidden rounded-sm"
            >
              {slices
                .filter((slice) => slice.drawn)
                .map((slice) => (
                  <span
                    key={slice.id}
                    data-slice={slice.id}
                    onClick={() => {
                      onChoose(slice.id);
                    }}
                    className={cn(
                      'h-full cursor-pointer transition-opacity',
                      slice.fill,
                      selected !== null &&
                        selected.id !== slice.id &&
                        'opacity-40',
                    )}
                    style={{ width: `${String(slice.share * 100)}%` }}
                  />
                ))}
            </div>
          ) : null}
          <ul
            aria-label={`Spend by ${meta.one}`}
            className="flex flex-wrap gap-x-3 gap-y-1"
          >
            {slices.map((slice) => (
              <li key={slice.id} className="min-w-0">
                <SliceToggle
                  slice={slice}
                  currency={currency}
                  pressed={selected?.id === slice.id}
                  controls={listId}
                  onPress={() => {
                    onChoose(slice.id);
                  }}
                />
              </li>
            ))}
          </ul>
          {selected ? (
            <SliceList
              id={listId}
              slice={selected}
              total={total}
              currency={currency}
              workspaceId={workspaceId}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function SliceToggle({
  slice,
  currency,
  pressed,
  controls,
  onPress,
}: {
  slice: Slice<Row>;
  currency: string;
  pressed: boolean;
  controls: string;
  onPress: () => void;
}) {
  const [line] = slice.lines;
  const spend = spendText(slice.lines, slice.amount, currency);
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-controls={pressed ? controls : undefined}
      onClick={onPress}
      data-slice-toggle={slice.id}
      className={SLICE_TOGGLE}
    >
      <span
        aria-hidden="true"
        data-slice-swatch=""
        className={cn('size-2.5 shrink-0 rounded-sm', slice.fill)}
      />
      {/* A profile's own mark beside its name: its colour is who it is, never how
          much (spec 251). */}
      {slice.kind !== 'others' && line.identity ? (
        <span aria-hidden="true" className="shrink-0">
          {line.identity}
        </span>
      ) : null}
      <span
        className={cn(
          'min-w-0 truncate',
          slice.kind === 'unknown' && 'text-muted-foreground italic',
        )}
        title={slice.kind === 'others' ? undefined : line.recorded}
      >
        {slice.label}
      </span>
      {slice.kind === 'named' && line.deleted ? <DeletedLabel /> : null}
      <span
        className={cn(
          'tabular-nums',
          spend.stated
            ? 'font-semibold'
            : 'text-muted-foreground text-xs font-normal',
        )}
      >
        {spend.text}
      </span>
      {slice.shareText ? (
        <span className="text-muted-foreground tabular-nums">
          {slice.shareText}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The chosen slice, listed (spec 255): a flat ranked list in place of the nested
 * trees. A line and, under a workflow, profile or connection, the lines beneath it,
 * ranked by spend as the service sends them; "N others", every line it holds.
 */
function SliceList({
  id,
  slice,
  total,
  currency,
  workspaceId,
}: {
  id: string;
  slice: Slice<Row>;
  total: number;
  currency: string;
  workspaceId: string;
}) {
  const [line] = slice.lines;
  const items =
    slice.kind === 'others'
      ? slice.lines.map((row) => ({ row, child: false }))
      : [
          { row: line, child: false },
          ...line.children.map((row) => ({ row, child: true })),
        ];
  return (
    <ul
      id={id}
      aria-label={slice.label}
      data-slice-list={slice.id}
      className="border-border divide-border divide-y rounded-md border"
    >
      {items.map(({ row, child }, index) => (
        <LineItem
          key={`${row.key ?? 'none'}-${String(index)}`}
          row={row}
          child={child}
          total={total}
          currency={currency}
          workspaceId={workspaceId}
        />
      ))}
    </ul>
  );
}

function LineItem({
  row,
  child,
  total,
  currency,
  workspaceId,
}: {
  row: Row;
  child: boolean;
  total: number;
  currency: string;
  workspaceId: string;
}) {
  const spend = spendText([row], row.amount, currency);
  const share = shareText(magnitude(row.amount), total);
  // What the line's runs leave out of the money (spec 253): said once, in words.
  const caveat = [
    row.unmeasured > 0 && row.unmeasured < row.runs
      ? `${String(row.unmeasured)} not measured`
      : null,
    row.unpriced > 0 && row.unmeasured + row.unpriced < row.runs
      ? `${String(row.unpriced)} unpriced`
      : null,
  ].filter(Boolean);
  return (
    <li
      data-line=""
      className={cn(
        'flex items-start justify-between gap-3 px-3 py-2 text-sm',
        child && 'bg-muted/20 ps-8',
        row.unknown && 'text-muted-foreground',
      )}
    >
      <span className="flex min-w-0 items-start gap-2">
        {row.marker ? (
          <span
            aria-hidden="true"
            className={cn('shrink-0', child ? 'mt-0.5' : 'max-sm:hidden')}
          >
            {row.marker}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {row.workflowKey ? (
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey"
                params={{ workspaceId, workflowKey: row.workflowKey }}
                className={cn(
                  LIST_ROW_LINK,
                  'min-w-0 break-words',
                  child && 'font-normal',
                )}
                title={row.recorded}
                data-slot="line-name"
              >
                {row.label}
              </Link>
            ) : (
              <span
                className={cn(
                  'min-w-0 break-words',
                  !child && 'font-medium',
                  row.unknown && 'italic',
                )}
                title={row.recorded}
                data-slot="line-name"
              >
                {row.label}
              </span>
            )}
            {row.deleted ? <DeletedLabel /> : null}
          </span>
          <span className="text-muted-foreground text-xs tabular-nums">
            {[
              runsLabel(row.runs),
              `${formatTokens(row.tokens)} billed tokens, with cache`,
              ...caveat,
            ].join(' · ')}
          </span>
          {row.hint ? (
            <span className="text-muted-foreground text-xs">{row.hint}</span>
          ) : null}
          <RowCap cap={row.cap} currency={currency} />
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end tabular-nums">
        <span
          className={cn(
            spend.stated
              ? !child && 'font-semibold'
              : 'text-muted-foreground text-xs',
          )}
        >
          {spend.text}
        </span>
        {share ? (
          <span className="text-muted-foreground text-xs">{share}</span>
        ) : null}
      </span>
    </li>
  );
}

/** The cap a workflow line is judged against; nothing at all where it has none. */
function RowCap({ cap, currency }: { cap: EntryCap | null; currency: string }) {
  // No cap renders nothing — not a zero and not "Within limit": "no cap" and
  // "comfortably under a cap" are different answers (spec 080 FR-014).
  if (cap === null) return null;
  return (
    <span className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted-foreground tabular-nums">
        {cap.own
          ? `${formatMoney(cap.own.spend, currency)} ${cap.own.phrase} `
          : null}
        of {formatMoney(cap.limit, currency)} limit
      </span>
      <span
        className={`${THRESHOLD_CHIP_BOX} ${THRESHOLD_CHIP[cap.threshold]}`}
      >
        {THRESHOLD_LABEL[cap.threshold]}
      </span>
    </span>
  );
}

/** Every dimension's lines as tables: the bars' non-visual route (spec 255). */
function AsTable({
  bars,
  currency,
}: {
  bars: { meta: DimensionMeta; rows: Row[] }[];
  currency: string;
}) {
  return (
    <Collapsible>
      <CollapsibleTrigger className={DISCLOSURE_TRIGGER}>
        <ChevronRightIcon aria-hidden="true" className={DISCLOSURE_CHEVRON} />
        Show as table
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="mt-2 flex flex-col gap-4">
          {bars
            .filter(({ rows }) => rows.length > 0)
            .map(({ meta, rows }) => {
              const total = rows.reduce(
                (sum, row) => sum + magnitude(row.amount),
                0,
              );
              return (
                <table
                  key={meta.value}
                  className="w-full table-fixed text-sm tabular-nums"
                >
                  <caption className="text-muted-foreground pb-1 text-left text-xs font-medium">
                    {`Spend by ${meta.one}`}
                  </caption>
                  <thead className="text-muted-foreground text-xs">
                    <tr>
                      <th scope="col" className="py-1 text-left font-medium">
                        {meta.title}
                      </th>
                      <th
                        scope="col"
                        className="hidden w-16 py-1 text-right font-medium sm:table-cell"
                      >
                        Runs
                      </th>
                      <th
                        scope="col"
                        className="hidden w-24 py-1 text-right font-medium sm:table-cell"
                      >
                        Tokens
                      </th>
                      <th
                        scope="col"
                        className="w-24 py-1 text-right font-medium"
                      >
                        Spend
                      </th>
                      <th
                        scope="col"
                        className="w-14 py-1 text-right font-medium"
                      >
                        Share
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={`${row.key ?? 'none'}-${String(index)}`}
                        className="border-border border-t"
                      >
                        <th
                          scope="row"
                          className="py-1 text-left font-normal break-words"
                        >
                          {row.label}
                          {row.deleted ? <DeletedLabel /> : null}
                        </th>
                        <td className="hidden py-1 text-right sm:table-cell">
                          {row.runs}
                        </td>
                        <td className="hidden py-1 text-right sm:table-cell">
                          {formatTokens(row.tokens)}
                        </td>
                        <td className="py-1 text-right">
                          {spendText([row], row.amount, currency).text}
                        </td>
                        <td className="text-muted-foreground py-1 text-right">
                          {shareText(magnitude(row.amount), total) ?? '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

function runsLabel(runs: number): string {
  return runs === 1 ? '1 run' : `${String(runs)} runs`;
}
